import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { humanizeCount } from 'helpers/humanizeCount';
import { Area, CartesianGrid, ComposedChart, Legend, Line, Tooltip, XAxis, YAxis } from 'recharts';
import { curveBumpX } from 'd3-shape';
import { useTheme } from 'styled-components';
import { Text } from 'rebass/styled-components';
import dayjs from 'dayjs';
import CustomEdgarTooltip from './customEdgarTooltip';

const BUMP_INT = curveBumpX;
const CHART_WIDTH = 450;

const FinancialGraph = ({ organizationFinancialInfos, workExpereinceInCompany, profileName }) => {
  const theme = useTheme();

  const years = useMemo(
    () =>
      workExpereinceInCompany.reduce((acc, duration) => {
        const start = dayjs(duration.dateFrom);
        const end = duration.dateTo === null ? dayjs(new Date()) : dayjs(duration.dateTo);

        const startYear = start.year();
        const endYear = end?.year() || startYear;

        if (!endYear && !startYear) {
          return acc;
        }
        const tenure = endYear - startYear;

        return [
          ...acc,
          startYear || endYear,
          ...[...Array(tenure > 0 ? tenure : 1)].map((_val, index) => startYear + index + 1),
          endYear,
        ];
      }, []),
    [workExpereinceInCompany],
  );

  const MISC_REVENUE_CHART_DEFAULT_LEGEND = [
    {
      id: 'square',
      type: 'square',
      value: profileName,
      color: '#75b5ff',
    },
    {
      id: 'revenue',
      type: 'line',
      value: 'Revenue',
      color: theme.colors.blue[600],
    },
    {
      id: 'netProfits',
      type: 'line',
      value: 'Net profits',
      color: theme.colors.green[600],
    },

    {
      id: 'grossProfits',
      type: 'line',
      value: 'Gross profits',
      color: theme.colors.neutral[400],
    },
    {
      id: 'totalCapitalization',
      type: 'line',
      value: 'Total capitalization',
      color: theme.colors.orange[600],
    },
  ];

  const EBITDA_CHART_DEFAULT_LEGEND = [
    {
      id: 'square',
      type: 'square',
      value: profileName,
      color: '#75b5ff',
    },
    {
      id: 'ebitda',
      type: 'line',
      value: 'EBITDA',
      color: theme.colors.orange[600],
    },
  ];

  const EPS_CHART_DEFAULT_LEGEND = [
    {
      id: 'square',
      type: 'square',
      value: profileName,
      color: '#75b5ff',
    },
    {
      id: 'basicEps',
      type: 'line',
      value: 'Basic eps',
      color: theme.colors.purple[300],
    },
  ];
  const units = [];
  const units1 = [];
  const units2 = [];

  const sortedFinancialInfos = [...organizationFinancialInfos].sort((a, b) => {
    const dateA = dayjs(a.periodEndDate);
    const dateB = dayjs(b.periodEndDate);
    if (dateA.isBefore(dateB)) {
      return -1;
    }
    if (dateA.isAfter(dateB)) {
      return 1;
    }
    return 0;
  });

  sortedFinancialInfos.forEach((info) => {
    const unitbar = Math.max(info.estRevenue, info.netIncome, info.grossProfit, info.marketCap);
    // TODO: bar is used to demark when the contact was in the company. if info has gaps then it's not displayed correctly.
    units.push({
      name: dayjs(info.periodEndDate).year(),
      revenue: info.estRevenue,
      netProfits: info.netIncome,
      grossProfit: info.grossProfit,
      totalCapitalization: info.marketCap,
      bar: years.includes(dayjs(info.periodEndDate).year()) ? unitbar : null,
    });
    units1.push({
      name: dayjs(info.periodEndDate).year(),
      ebitda: info.ebitda,
      bar: years.includes(dayjs(info.periodEndDate).year()) ? info.ebitda : null,
    });
    units2.push({
      name: dayjs(info.periodEndDate).year(),
      basicEps: info.basicEps,
      bar: years.includes(dayjs(info.periodEndDate).year()) ? info.basicEps : null,
    });
  });

  const isUnitsEmpty = units.every(
    (unit) => !unit.revenue && !unit.netProfits && !unit.assets && !unit.grossProfit && !unit.totalCapitalization,
  );
  const isUnits1Empty = units1.every((unit) => !unit.ebitda);
  const isUnits2Empty = units2.every((unit) => !unit.basicEps);
  const isAnyUnitNotEmpty = !isUnitsEmpty || !isUnits1Empty || !isUnits2Empty;

  return (
    <div style={{ marginBottom: isAnyUnitNotEmpty ? '16px' : '0' }}>
      {isAnyUnitNotEmpty && (
        <Text
          fontSize="12px"
          fontWeight="medium"
          marginBottom="8px"
          sx={{ color: 'text.subtle', letterSpacing: '1px', textTransform: 'uppercase' }}
        >
          Financial Graph
        </Text>
      )}
      {!isUnitsEmpty && (
        <ComposedChart
          width={CHART_WIDTH}
          height={225}
          data={units}
          margin={{ top: 10, right: 15, bottom: 0, left: 0 }}
        >
          <CartesianGrid stroke="#f5f5f5" />
          <XAxis
            interval="preserveStartEnd"
            // minTickGap={1}
            tickLine={false}
            tick={{ fontSize: 10 }}
            dataKey="name"
          />
          <YAxis
            tickLine={false}
            tick={{ fontSize: 10 }}
            tickFormatter={
              // number | string is okay
              humanizeCount
            }
          />
          <defs>
            <linearGradient id="colorTenure" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="rgb(117, 181, 255)" stopOpacity={0.6} />
              <stop offset="100%" stopColor="rgb(117, 181, 255)" stopOpacity={0.1} />
            </linearGradient>
          </defs>
          {/* @ts-ignore */}
          <Tooltip content={<CustomEdgarTooltip />} />
          <Area type={BUMP_INT} dataKey="bar" stroke="transparent" fillOpacity={1} fill="url(#colorTenure)" />
          <Line
            type={BUMP_INT}
            dot={false}
            strokeWidth="2"
            dataKey="revenue"
            stroke={theme.colors.blue[600]}
            connectNulls
          />
          <Line
            type={BUMP_INT}
            dot={false}
            strokeWidth="2"
            dataKey="netProfits"
            stroke={theme.colors.green[600]}
            connectNulls
          />
          <Line
            type={BUMP_INT}
            dot={false}
            strokeWidth="2"
            dataKey="grossProfits"
            stroke={theme.colors.neutral[400]}
            connectNulls
          />
          <Line
            type={BUMP_INT}
            dot={false}
            strokeWidth="2"
            dataKey="totalCapitalization"
            stroke={theme.colors.orange[600]}
            connectNulls
          />
          <Legend
            align="center"
            verticalAlign="bottom"
            payload={MISC_REVENUE_CHART_DEFAULT_LEGEND}
            formatter={(value) => (
              <Text style={{ display: 'inline-block' }} fontSize="14px" fontWeight="medium" color="text.main">
                {value}
              </Text>
            )}
          />
        </ComposedChart>
      )}
      {/* add gap */}
      <div style={{ height: 20 }} />
      {/* chart 2 */}
      {!isUnits1Empty && (
        <ComposedChart
          width={CHART_WIDTH}
          height={225}
          data={units1}
          margin={{ top: 10, right: 15, bottom: 0, left: 0 }}
        >
          <CartesianGrid stroke="#f5f5f5" />
          <XAxis
            interval="preserveStartEnd"
            // minTickGap={1}
            tickLine={false}
            tick={{ fontSize: 10 }}
            dataKey="name"
          />
          <YAxis
            tickLine={false}
            tick={{ fontSize: 10 }}
            tickFormatter={
              // number | string is okay
              humanizeCount
            }
          />
          <defs>
            <linearGradient id="colorTenure" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="rgb(117, 181, 255)" stopOpacity={0.6} />
              <stop offset="100%" stopColor="rgb(117, 181, 255)" stopOpacity={0.1} />
            </linearGradient>
          </defs>
          {/* @ts-ignore */}
          <Tooltip content={<CustomEdgarTooltip />} />
          <Area type={BUMP_INT} dataKey="bar" stroke="transparent" fillOpacity={1} fill="url(#colorTenure)" />
          <Line
            type={BUMP_INT}
            dot={false}
            strokeWidth="2"
            dataKey="ebitda"
            stroke={theme.colors.orange[600]}
            connectNulls
          />
          <Legend
            align="center"
            verticalAlign="bottom"
            payload={EBITDA_CHART_DEFAULT_LEGEND}
            formatter={(value) => (
              <Text style={{ display: 'inline-block' }} fontSize="14px" fontWeight="medium" color="text.main">
                {value}
              </Text>
            )}
          />
        </ComposedChart>
      )}
      {/* add gap */}
      <div style={{ height: 20 }} />
      {/* chart 3 */}
      {!isUnits2Empty && (
        <ComposedChart
          width={CHART_WIDTH}
          height={225}
          data={units2}
          margin={{ top: 10, right: 15, bottom: 0, left: 0 }}
        >
          <CartesianGrid stroke="#f5f5f5" />
          <XAxis
            interval="preserveStartEnd"
            // minTickGap={1}
            tickLine={false}
            tick={{ fontSize: 10 }}
            dataKey="name"
          />
          <YAxis
            tickLine={false}
            tick={{ fontSize: 10 }}
            tickFormatter={
              // number | string is okay
              humanizeCount
            }
          />
          <defs>
            <linearGradient id="colorTenure" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="rgb(117, 181, 255)" stopOpacity={0.6} />
              <stop offset="100%" stopColor="rgb(117, 181, 255)" stopOpacity={0.1} />
            </linearGradient>
          </defs>
          {/* @ts-ignore */}
          <Tooltip content={<CustomEdgarTooltip />} />
          <Area type={BUMP_INT} dataKey="bar" stroke="transparent" fillOpacity={1} fill="url(#colorTenure)" />
          <Line
            type={BUMP_INT}
            dot={false}
            strokeWidth="2"
            dataKey="basicEps"
            stroke={theme.colors.purple[300]}
            connectNulls
          />
          <Legend
            align="center"
            verticalAlign="bottom"
            payload={EPS_CHART_DEFAULT_LEGEND}
            formatter={(value) => (
              <Text style={{ display: 'inline-block' }} fontSize="14px" fontWeight="medium" color="text.main">
                {value}
              </Text>
            )}
          />
        </ComposedChart>
      )}
    </div>
  );
};

FinancialGraph.propTypes = {
  organizationFinancialInfos: PropTypes.array.isRequired,
  workExpereinceInCompany: PropTypes.array.isRequired,
  profileName: PropTypes.string.isRequired,
};

export default FinancialGraph;
